import { Box, Button, Grid, Paper } from '@mui/material';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import SearchField from 'src/shared/components/form/searchField';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { AddCircle } from '@mui/icons-material';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { InvoiceAddressSearchFilter } from '../models/invoiceAddressSearchFilter';
import { InvoiceAddressDataGrid } from '../components/invoiceAddressDataGrid';
import { InvoiceAddressCreateModal } from '../components/invoiceAddressCreateModal';

export const InvoiceAddressPage = () => {
  const isMounted = useIsMounted();

  const [createMode, setCreateMode] = useState<boolean>(false);

  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [invoiceAddressSearchFilter, setInvoiceAddressSearchFilter] = useLocalStorage(LocalStorageKeys.InvoiceAddressSearchFilter, {
    sortCol: 'invoiceAddress_id',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as unknown as InvoiceAddressSearchFilter);

  const searchFilter: InvoiceAddressSearchFilter = useMemo(() => {
    return {
      filter: invoiceAddressSearchFilter?.filter,
      pageSize: invoiceAddressSearchFilter?.pageSize,
      pageIndex: invoiceAddressSearchFilter?.pageIndex,
      sortCol: invoiceAddressSearchFilter?.sortCol,
      sortDir: invoiceAddressSearchFilter?.sortDir,
    } as InvoiceAddressSearchFilter;
  }, [
    invoiceAddressSearchFilter?.filter,
    invoiceAddressSearchFilter?.pageSize,
    invoiceAddressSearchFilter?.pageIndex,
    invoiceAddressSearchFilter?.sortCol,
    invoiceAddressSearchFilter?.sortDir,
  ]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setInvoiceAddressSearchFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setInvoiceAddressSearchFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setInvoiceAddressSearchFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setInvoiceAddressSearchFilter, tempSearchString]);

  return (
    <>
      <GridWrapper>
        <Grid item xs='auto'>
          <MainContentHeader title={t('member:invoiceAddress.pageHeader')} />
        </Grid>
        <Grid item xs sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
            <Button variant='contained' color='secondary' startIcon={<AddCircle />} onClick={() => setCreateMode(true)} disableElevation>
              {t('member:invoiceAddress.createButton')}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <PageFilterSection
              leftContent={
                <SearchField
                  initialValue={searchFilter.filter || ''}
                  onSearch={(newSearchString) => {
                    setSearchInitialized(true);
                    setTempSearchString(newSearchString);
                  }}
                />
              }
            />
            <InvoiceAddressDataGrid searchFilter={searchFilter} />
          </Paper>
        </Grid>
      </GridWrapper>
      {createMode && <InvoiceAddressCreateModal onClose={() => setCreateMode(false)} modalOpen={createMode} />}
    </>
  );
};
